import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, FileText, Ship } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { ExpiryBadge, EXPIRY_META } from "@/components/VesselDocuments";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CandidateExpiry } from "@/components/CandidateExpiry";

const CANDIDATE_ROLES = ["Super Admin", "Office Admin", "Technical Superintendent/Assessment", "Documentation/Operations", "Management/HOD"];

export default function ExpiryBoardPage() {
  const { user } = useAuth();
  const showCandidates = CANDIDATE_ROLES.includes(user?.role?.role_name) || user?.role?.domain === "Sourcing";
  if (!showCandidates) return <FleetDocsBoard />;
  return (
    <Tabs defaultValue="vessels" data-testid="expiry-board-tabs">
      <TabsList className="mb-4 flex-wrap h-auto justify-start">
        <TabsTrigger value="vessels" data-testid="expiry-tab-vessels">Vessel Documents</TabsTrigger>
        <TabsTrigger value="candidates" data-testid="expiry-tab-candidates" className="whitespace-normal">Candidates with Expiring Docs</TabsTrigger>
      </TabsList>
      <TabsContent value="vessels"><FleetDocsBoard /></TabsContent>
      <TabsContent value="candidates"><CandidateExpiry /></TabsContent>
    </Tabs>
  );
}

const FILTERS = [["all", "All"], ["expired", "Expired"], ["expiring_soon", "Expiring Soon"]];
const TOTAL_ORDER = ["expired", "red", "orange", "green", "missing"];
const FILTER_ROLES = ["Super Admin", "Management/HOD"];

function Totals({ totals }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="board-totals">
      {TOTAL_ORDER.map((k) => (
        <div key={k} className="bg-white border border-slate-200 rounded-xl p-4">
          <ExpiryBadge status={k} />
          <div className="text-2xl font-bold text-slate-900 font-mono mt-2" data-testid={`board-total-${k}`}>{totals?.[k] ?? 0}</div>
        </div>
      ))}
    </div>
  );
}

function DocLine({ d }) {
  return (
    <li className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-t border-slate-100" data-testid={`board-doc-${d.id}`}>
      <div className="min-w-0">
        <div className="font-medium text-slate-800 truncate">{d.document_name}</div>
        <div className="text-xs text-slate-400 font-mono mt-0.5">
          {d.expiry_date ? `${d.expiry_date} · ${d.days_left < 0 ? `${-d.days_left} days ago` : `${d.days_left} days left`}` : "No expiry date"}
        </div>
      </div>
      <ExpiryBadge status={d.expiry_status} testid={`board-doc-status-${d.id}`} />
    </li>
  );
}

function VesselCard({ v }) {
  const counts = TOTAL_ORDER.map((k) => [k, v.documents.filter((d) => d.expiry_status === k).length]).filter(([, n]) => n);
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`board-vessel-${v.id}`}>
      <div className="px-4 sm:px-5 py-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Ship className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <Link to={`/vessels/${v.id}`} className="font-semibold text-slate-900 hover:text-cyan-700 transition-colors truncate" data-testid={`board-vessel-link-${v.id}`}>{v.vessel_name}</Link>
          <span className="text-xs text-slate-400 truncate">{v.owner_name}</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {counts.map(([k, n]) => (
            <span key={k} className={`text-[11px] px-2 py-0.5 rounded-full border ${EXPIRY_META[k].cls}`}>{n} {EXPIRY_META[k].label}</span>
          ))}
        </div>
      </div>
      {v.documents.length ? (
        <ul>{v.documents.map((d) => <DocLine key={d.id} d={d} />)}</ul>
      ) : (
        <div className="px-5 py-4 border-t border-slate-100 text-sm text-slate-400" data-testid={`board-vessel-empty-${v.id}`}>No documents uploaded yet</div>
      )}
    </div>
  );
}

function FleetDocsBoard() {
  const { user } = useAuth();
  const canFilter = FILTER_ROLES.includes(user?.role?.role_name);
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/documents/board", { params: { filter } })
      .then(({ data: d }) => setData(d))
      .catch((err) => { toast.error(formatApiError(err)); setData({ totals: {}, vessels: [] }); });
  }, [filter]);

  return (
    <div className="space-y-6" data-testid="expiry-board-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Fleet</div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>Expiry Board</h1>
          <div className="text-sm text-slate-500 mt-1">Document health across every vessel, most urgent first.</div>
        </div>
        {canFilter && (
          <div className="flex gap-1.5 bg-white border border-slate-200 rounded-lg p-1" data-testid="board-filters">
            {FILTERS.map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`board-filter-${k}`}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors ${filter === k ? "bg-[#0b192c] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                {l}
              </button>
            ))}
          </div>
        )}
      </div>

      {!data ? (
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 inline" /></div>
      ) : (
        <>
          <Totals totals={data.totals} />
          <div className="space-y-4">
            {data.vessels.map((v) => <VesselCard key={v.id} v={v} />)}
          </div>
          {data.vessels.length === 0 && (
            <div className="py-14 text-center" data-testid="board-empty-state">
              <FileText className="w-8 h-8 text-slate-300 mx-auto" />
              <div className="text-sm text-slate-500 mt-2">{filter === "all" ? "No vessels to show yet." : "No documents match this filter."}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
